import type { ReactElement } from 'react';

import { memo } from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

import { NavLink } from './NavLink';
import { media } from '../../core/styles';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { getCategoryPathsFromPDPUrl } from '~/utilities/ssr';

const S = {
  NavLinks: styled.nav`
    ${({ theme }) => css`
      display: none;
      height: var(--top-nav-height);
      left: 70px;
      position: absolute;
      top: 0;
      white-space: nowrap;

      @media ${media(theme).greaterThan('sm')} {
        display: contents;
      }

      @media ${media(theme).greaterThan('lg')} {
        position: absolute;
        padding-left: 0;
      }
    `}
  `,

  NavLinkList: styled.ul`
    display: flex;
    height: 100%;
    list-style: none;
    margin: 0;
    padding: 0;
  `,

  NavLinkListItem: styled.li`
    ${({ theme }) => css`
      display: flex;

      &:first-child {
        a {
          padding-left: ${theme.padding.none};
        }
      }

      @media ${media(theme).lessThan('lg')} {
        &:nth-child(n + 3) {
          display: none;
        }
      }
    `}
  `,
};

const NavLinksComponent = (): ReactElement => {
  const { navLinks, accessibilityLabels } = useStaticContext();
  const primaryNavigation = accessibilityLabels?.primaryNavigation;

  const {
    product: { url },
  } = usePDPContext();

  const { categoryDetails, isPLP } = useProductListContext();

  const currentUrl = isPLP ? categoryDetails?.requestUrl : url;
  const { superCategory } = getCategoryPathsFromPDPUrl(currentUrl);

  return (
    <S.NavLinks aria-label={primaryNavigation ?? 'Primary navigation'} data-cs-capture="">
      <S.NavLinkList>
        {navLinks.map(link => (
          <S.NavLinkListItem key={link?.uid}>
            <NavLink link={link} superCategory={superCategory} />
          </S.NavLinkListItem>
        ))}
      </S.NavLinkList>
    </S.NavLinks>
  );
};

export const NavLinks = memo(NavLinksComponent, isEqual);
